import { Button, Col, Container, Row, Table } from "react-bootstrap";
import { useCallback, useEffect, useRef, useState } from "react";

import { AppTopNav } from "app/modules/shared/AppTopNav";
import { BasePageProps, BoothState } from "app/types";
import BoothCard from "app/modules/shared/booth-card/BoothCard";
import { BoothService } from "app/services/BoothService";
import { QueueUserService } from "app/services/QueueUserService";
import { useAppContext } from "app/context/app-context";
import { useAuth } from "app/context/auth-context";
import { useHistory, useLocation } from "react-router-dom";
import { Booth, BoothJobInfo, EndReason, VideoSessionStatus } from 'app/API';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import { UrlParamReplace } from "app/helpers/MiscUtils";
import { JSRoutes } from "../JSPage";
import useIsMounted from "app/hooks/useIsMounted";
import { AlertModal } from "app/modules/shared/modals/AlertModal";
import { RxSubscription } from "app/RxSubscriptions";
import { Amplify } from 'aws-amplify';
import { Modal } from "react-bootstrap-v5";
import { OrgService } from "app/services/OrgService";
import { useS3Object } from "app/context/S3ObjectContext";
Amplify.configure({
    Storage: {
        AWSS3: {
            region: 'us-east-1',
            bucket: 'videojobfairs-docs'
        }
    }
});

const JSLobby = (props: BasePageProps) => {
    const { user } = useAuth();
    const { jobFair } = useAppContext();
    const [booths, setBooths] = useState<Booth[]>([]);
    const [boothState, setBoothState] = useState<BoothState[]>([]);
    const boothService = new BoothService();
    const queueUserService = new QueueUserService();
    const history = useHistory();
    const [loading, setLoading] = useState<boolean>(true);
    const isMounted = useIsMounted('JSLobby');
    const [isEndedByRecruiter, setIsEndedByRecruiter] = useState<boolean>(false);
    const [endReason, setEndReason] = useState<EndReason | undefined | null>();
    const [showJobsModal, setShowJobsModal] = useState<boolean>(false)
    const [jobs, setJobs] = useState<BoothJobInfo[]>([])
    const [orgLogoKey, setOrgLogoKey] = useState<string | undefined>();
    const { getS3ObjectUrl, s3ObjectCache } = useS3Object();
    useEffect(() => {
        if (!jobFair?.id) {
            return;
        }
        setLoading(true);
        (async () => {
            const _booths = await boothService.getAllBooths(jobFair.id);

            if (isMounted()) {
                setBooths(_booths.sort((a, b) => (a?.sortOrder || 0) - (b?.sortOrder || 0)) as Booth[]);
                setLoading(false);
                if (_booths?.length === 1) {
                    onBoothDetail(_booths[0].id);
                }
            }

            // Fetch organiation logo
            const org: any = await new OrgService().getOrgById(jobFair.companyId);
            if (org?.companyLogo) {
                setOrgLogoKey(org.companyLogo);
                getS3ObjectUrl(org.companyLogo);
            }
        })();
    }, [jobFair?.id, isMounted, jobFair?.companyId]);

    // Set required Booth State for company logo
    useEffect(() => {
        if (!booths?.length)
            return;

        const _boothStates = [...boothState];
        (async () => {
            await Promise.all(booths.map(async b => {
                try {
                    const bs = _boothStates.find(s => s.boothId == b.id);
                    if (b.companyImageUrl) {
                        getS3ObjectUrl(b.companyImageUrl);
                    }

                    if (!bs)
                        _boothStates.push({ boothId: b.id, isSubscribed: false, companyImageUrl: '' })
                }
                catch (err) {
                    console.log('logo missing for company id' + b?.id);
                }
            }));
            if (isMounted())
                setBoothState(_boothStates);
            return true;
        })();
    }, [booths, isMounted])

    const removeInQueueStatus = useCallback(
        (boothId: string) => {
            setBoothState(prev => {
                const bt = prev.find(x => x.boothId === boothId);
                if (bt) {
                    bt.isSubscribed = false;
                }
                return [...prev];
            })
        },
        [boothState],
    )

    const setInQueueStatus = useCallback(
        async () => {
            if (!jobFair?.id || !user?.id || !booths?.length || !boothState?.length)
                return;

            const subscribedBoothIds = await queueUserService.getAllSubscribedBoothsByJobSeeker(jobFair.id, user.id);
            if (!isMounted()) return;
            const _boothStates = [...boothState];
            _boothStates.filter(x => !subscribedBoothIds.includes(x.boothId)).forEach(bs => {
                bs.isSubscribed = false;
            });

            subscribedBoothIds.forEach(x => {
                const bs = (_boothStates.find((y) => y?.boothId === x)) as BoothState;
                if (bs) {
                    bs.isSubscribed = true;
                }
                else
                    _boothStates.push({ boothId: x, isSubscribed: true, companyImageUrl: '' });
            });
            setBoothState(_boothStates);
        },
        [booths, boothState, user, isMounted]
    )

    // Set required Booth State If user is Entered or Subscribed to a Booth
    useEffect(() => {
        if (!jobFair?.id || !user?.id || !boothState?.length)
            return;
        RxSubscription.onChangeInterviewSessionByCandidateIdObservable().subscribe(async (event) => {
            if (event && event.mode === 'update' && event.value.status === VideoSessionStatus.ENDED && isMounted()) {
                if (event.value.endedBy !== user.id)
                    setEndReason(event?.value?.endReason);
                setIsEndedByRecruiter(true);
                setTimeout(() => {
                    removeInQueueStatus(event.value.boothId);
                    //  window.location.reload();
                }, 2000);

                RxSubscription.clearInterviewSessionByCandidateId();
            }
        });
        (async () => await setInQueueStatus())();
    }, [jobFair?.id, user?.id, boothState?.length, isMounted]);

    const getBoothState = useCallback(
        (boothId: string) => {
            return boothState.find(bs => bs.boothId === boothId);
        },
        [boothState]
    );

    const onBoothDetail = (boothId: string) => {
        history.push({
            pathname: UrlParamReplace(JSRoutes.BoothDetail, ':boothId', boothId),
            state: {
                boothState: getBoothState(boothId),
                booth: booths.find(b => b.id === boothId)
            }
        });
    }

    return (
        <>
            <div className="chatPage">
                <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs}>
                </AppTopNav>
                <BlockUi id="boothLoadingContainer" tag="div" className="container-fluid" blocking={loading}>
                    <Container className="mt-4 lobbyCard">
                        <Row>
                            {booths && booths.map((b, index) => {
                                const _bs = getBoothState(b.id);
                                return (<Col className="col-md-4" key={"card-" + index} >
                                    <BoothCard isFeatured={b.isFeatured} logo={b.companyImageUrl ? s3ObjectCache[b.companyImageUrl] : (orgLogoKey ? s3ObjectCache[orgLogoKey] : '')}>
                                        <BoothCard.Title isInQueue={_bs?.isSubscribed}>{b.name}</BoothCard.Title>
                                        <BoothCard.Description>{(b.description?.length || 0) > 150 ? b.description?.substr(0, 150) + '...' : (b.description) || ''}</BoothCard.Description>
                                        <div className="cardBodyWrap">
                                            <BoothCard.Body onClick={(key) => {
                                                switch (key) {
                                                    case "Jobs":
                                                        setShowJobsModal(true);
                                                        setJobs(b.jobs);
                                                        break;
                                                    default:
                                                        break;
                                                }
                                            }} jobsCount={b.jobs?.length || 0} recruitersCount={b.recruiters?.length || 0} >
                                                <Button id={"boothBtn" + b.name.replaceAll(' ', '')} variant="success" className="w-100 mt-3 py-3" onClick={() => { onBoothDetail(b.id) }}>Enter Booth</Button>
                                            </BoothCard.Body>
                                        </div>
                                    </BoothCard>
                                </Col>);
                            })
                            }
                        </Row>
                    </Container>
                </BlockUi>
            </div>
            <AlertModal title="Interview has ended"
                show={isEndedByRecruiter}
                onOK={async () => {
                    setIsEndedByRecruiter(false);
                }}>
                {endReason === EndReason.RecCLosedBrowser && <span>Recruiter experienced Internet connectivity issue or ended the interview.</span>}
            </AlertModal>
            <Modal
                show={showJobsModal}
                onHide={async () => {
                    setShowJobsModal(false);
                }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Jobs</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table
                        className="table table-bordered table-striped border"
                        style={{ borderColor: '#dee2e6' }}
                        hover={true}
                    >
                        <thead className="border-bottom">
                            <tr>
                                <th className="border-end fw-bolder">#</th>
                                <th className="fw-bolder">Job description</th>
                                <th className="fw-bolder">Job Link</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs?.map((job, index) => {
                                return (
                                    <tr key={"job-" + index}>
                                        <td className="border-end fw-semibold">{index + 1}</td>
                                        <td className="fw-semibold">{job.description}</td>
                                        <td className="fw-semibold"> {job.link && <a href={job.link} target='_blank'> Link</a>}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={async () => {
                        setShowJobsModal(false);
                    }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
export default JSLobby;